import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  Paper,
  Alert,
  AlertTitle,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Settings as SettingsIcon,
  Analytics as AnalyticsIcon,
  SyncAlt as SyncIcon,
  MapOutlined as MappingIcon,
  Build as BuildIcon,
  Assessment as MOEIcon,
  School as UniversityIcon,
  Map as MapAnalysisIcon,
} from '@mui/icons-material';

// Import tab components
import SISDashboard from './components/SISDashboard';
import SISConfigurations from './components/SISConfigurations';
import CustomIndicators from './components/CustomIndicators';
import DataMapping from './components/DataMapping';
import SyncLogs from './components/SyncLogs';
import Analytics from './components/Analytics';
import IndicatorBuilder from './components/IndicatorBuilder';
import MOEIndicatorBuilder from './components/MOEIndicatorBuilder';
import UniversityEnrollmentForm from './components/UniversityEnrollmentForm';
import AdvancedAnalysis from './components/AdvancedAnalysis';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`sis-tabpanel-${index}`}
      aria-labelledby={`sis-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `sis-tab-${index}`,
    'aria-controls': `sis-tabpanel-${index}`,
  };
}

const SISIntegration: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          SIS Integration
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage Student Information System integration, custom indicators, and real-time data synchronization
        </Typography>
      </Box>

      {/* Info Alert */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <AlertTitle>Live Integration Active</AlertTitle>
        Connected to live SIS database with real-time synchronization capabilities. 
        Monitor sync status and manage custom indicators from this dashboard.
      </Alert>

      <Paper elevation={1}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="SIS Integration tabs"
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab
              icon={<DashboardIcon />}
              label="Dashboard"
              {...a11yProps(0)}
            />
            <Tab
              icon={<SettingsIcon />}
              label="Configurations"
              {...a11yProps(1)}
            />
            <Tab
              icon={<AnalyticsIcon />}
              label="Custom Indicators"
              {...a11yProps(2)}
            />
            <Tab
              icon={<MappingIcon />}
              label="Data Mapping"
              {...a11yProps(3)}
            />
            <Tab
              icon={<SyncIcon />}
              label="Sync Logs"
              {...a11yProps(4)}
            />
            <Tab
              icon={<BuildIcon />}
              label="Indicator Builder"
              {...a11yProps(5)}
            />
            <Tab
              icon={<MOEIcon />}
              label="MOE Indicators"
              {...a11yProps(6)}
            />
            <Tab
              icon={<UniversityIcon />}
              label="Universities"
              {...a11yProps(7)}
            />
            <Tab
              icon={<AnalyticsIcon />}
              label="Analytics"
              {...a11yProps(8)}
            />
            <Tab
              icon={<MapAnalysisIcon />}
              label="Advanced Analysis"
              {...a11yProps(9)}
            />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <SISDashboard />
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <SISConfigurations />
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <CustomIndicators />
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          <DataMapping />
        </TabPanel>

        <TabPanel value={tabValue} index={4}>
          <SyncLogs />
        </TabPanel>

        <TabPanel value={tabValue} index={5}>
          <IndicatorBuilder />
        </TabPanel>

        <TabPanel value={tabValue} index={6}>
          <MOEIndicatorBuilder />
        </TabPanel>

        <TabPanel value={tabValue} index={7}>
          <UniversityEnrollmentForm />
        </TabPanel>

        <TabPanel value={tabValue} index={8}>
          <Analytics />
        </TabPanel>

        <TabPanel value={tabValue} index={9}>
          <AdvancedAnalysis />
        </TabPanel>
      </Paper>
    </Container>
  );
};

export default SISIntegration;